import { z } from 'zod';

/**
 * Phase 4 chunk 10 (closing) — Trust Center timeline DTOs.
 *
 * Mirror of TrustCenterService.timeline() projection.  Aggregates
 * governance events from three sources into a single sorted feed:
 *   - approval_created   — a new approval landed
 *   - approval_resolved  — approval transitioned to terminal state
 *   - approval_decision  — an approver action was recorded (decide)
 *   - policy_changed     — a policy version was bumped (new or edit)
 *
 * Each event carries enough context to render the row without
 * additional fetches.  FE links into the existing /approvals and
 * /policies modals for full detail.
 */

export const TrustCenterEventKind = z.enum([
  'approval_created',
  'approval_resolved',
  'approval_decision',
  'policy_changed',
  /** Phase 5 chunk 9 — workflow state change (per-team hash-chained).
   *  Sources: 'change_status' (in-band write), 'approval_apply'
   *  (resolved approval re-applies the move), 'workflow_migration'
   *  (bulk re-map via WorkflowMigrationService).  `meta.source`
   *  carries the discriminator. */
  'task_status_changed',
]);
export type TrustCenterEventKindValue = z.infer<typeof TrustCenterEventKind>;

export const TrustCenterEventSchema = z.object({
  kind: TrustCenterEventKind,
  /** ISO timestamp the event occurred (created_at / resolved_at /
   *  decided_at / changed_at depending on kind). */
  occurredAt: z.string().datetime(),
  /** Stable id of the related row (approval_id / decision_id /
   *  policy_version_id). */
  refId: z.string().uuid(),
  /** Optional secondary id (e.g. approval_id when kind=
   *  approval_decision — lets FE link back to the parent). */
  parentId: z.string().uuid().nullable(),
  /** One-line summary the timeline row renders. */
  summary: z.string(),
  /** Actor responsible — null for system-driven events (TTL expire,
   *  policy seed). */
  actorId: z.string().uuid().nullable(),
  /** Free-form bag for kind-specific extras (status, decision verb,
   *  policy name, etc).  FE renders selectively per kind. */
  meta: z.record(z.unknown()),
});
export type TrustCenterEvent = z.infer<typeof TrustCenterEventSchema>;

export const TrustCenterTimelineResponseSchema = z.object({
  items: z.array(TrustCenterEventSchema),
});
export type TrustCenterTimelineResponse = z.infer<
  typeof TrustCenterTimelineResponseSchema
>;
